import { useState, useRef } from "react"
import ReactSelect from "react-select"
import CreatableSelect from 'react-select/creatable'
import HiddenInput from "./HiddenInput"
import useT from "utils/hooks/useT"

// Options is a list of {label, value} objects
// Value is the selected values
export default function Select({
  name,
  required,
  disabled,
  attr = "name",
  collection,
  value,
  onChange,
  menuPosition = "fixed",
  menuPlacement = undefined,
  isMulti = true,
  closeMenuOnSelect,
  allowSelectAll=false,
  includeNone = false, // New prop to control inclusion of "None"
  noneLabel = undefined, // Custom label for the "none" option
  creatable = false, // If true, allow creating new options by typing
}) {
  const [inputValue, setInputValue] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const selectRef = useRef(null)
  const isSelectingRef = useRef(false)
  const { t } = useT("common")
  // normalize value to array for internal checks
  const selectedValues = value === undefined || value === null ? [] : Array.isArray(value) ? value : [value]
  // Alternative to `menuPosition="fixed"`:
  // menuPosition="relative"
  // styles={{ menu: (provided, state) => ({ ...provided, position: "relative" }) }}
  let collectionAdj = collection.map((item) => ({ label: item[attr], value: item.id }))
  if(allowSelectAll) {
    collectionAdj = [{ label: t("selectAll"), value: "ALL" }, ...collectionAdj]
  }
  if (includeNone) {
    const noneLabelText = noneLabel !== undefined ? noneLabel : t("none")
    collectionAdj = [{ label: noneLabelText, value: "" }, ...collectionAdj]
  }

  // For creatable selects we need to ensure any currently selected values that
  // are not part of the collection are included as options so ReactSelect can
  // render them as selected. We'll add them with label equal to the value.
  if (creatable) {
    const missing = selectedValues.filter((v) => v !== undefined && v !== null && !collectionAdj.some((c) => c.value === v))
    if (missing.length > 0) {
      collectionAdj = [...collectionAdj, ...missing.map((v) => ({ label: String(v), value: v }))]
    }
  }
  // Handler to automatically create a new option when user types and blurs without selecting
  const handleBlur = () => {
    // Don't save if user is selecting from dropdown
    if (isSelectingRef.current) {
      return
    }
    
    if (creatable && isEditing) {
      if (inputValue && inputValue.trim() !== "") {
        // Check if the inputValue already exists in the collection
        const exists = collectionAdj.some((opt) => opt.value === inputValue || opt.label === inputValue)
        if (!exists) {
          // Auto-create the new value
          if (isMulti) {
            const newValues = [...selectedValues, inputValue]
            onChange(newValues)
          } else {
            onChange([inputValue])
          }
        }
        setInputValue("")
        setIsEditing(false)
      } else if (inputValue.trim() === "") {
        // If user cleared the input while editing, save the empty value
        if (!isMulti) {
          onChange([])
        }
        setInputValue("")
        setIsEditing(false)
      }
    }
  }

  // When user starts typing in single-select creatable mode, populate input with current value for editing
  const handleInputChange = (newValue, actionMeta) => {
    if (actionMeta.action === "input-change") {
      setInputValue(newValue)
      // Always mark as editing when user types
      if (!isMulti) {
        setIsEditing(true)
      }
    } else if (actionMeta.action === "input-blur" || actionMeta.action === "menu-close") {
      // Don't clear input on blur - let handleBlur save it first
    }
  }

  // When user focuses on the field, populate input with current value if in single-select mode
  const handleFocus = () => {
    if (creatable && !isMulti && selectedValues.length > 0) {
      // Find the label for the current value
      const currentOption = collectionAdj.find((opt) => opt.value === selectedValues[0])
      if (currentOption) {
        setInputValue(currentOption.label)
        setIsEditing(true)
      }
    }
  }

  // Clear input when menu closes
  const handleMenuClose = () => {
    if (creatable && isEditing) {
      setInputValue("")
      setIsEditing(false)
    }
  }

  return (
    <div className="HiddenInput-container Select">
      {creatable ? (
        <CreatableSelect
          ref={selectRef}
          // Make it obvious the field is editable
          isSearchable={true}
          placeholder={t("typeOrSelect")}
          formatCreateLabel={(inputValue) => inputValue}
          isMulti={isMulti}
          isClearable={false}
          closeMenuOnSelect={closeMenuOnSelect}
          name={name}
          isDisabled={disabled}
          menuPosition={menuPosition}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
            control: (base) => ({ ...base, cursor: "text" }),
            input: (base) => ({ ...base, caretColor: "#111" }),
            dropdownIndicator: (base) => ({ ...base, cursor: "default" }),
          }}
          options={collectionAdj}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMenuClose={handleMenuClose}
          onChange={(items) => {
            // Mark that user is selecting to prevent handleBlur from interfering
            isSelectingRef.current = true
            
            const newValues = items ? (isMulti ? items.map((item) => item.value) : [items.value]) : []
            onChange(newValues)
            // Clear editing state completely when user explicitly selects an option
            setInputValue("")
            setIsEditing(false)
            // Blur the select to ensure the field loses focus after selection
            if (selectRef.current && !isMulti) {
              selectRef.current.blur()
            }
            
            // Reset the flag immediately
            isSelectingRef.current = false
          }}
          value={isEditing && !isMulti && inputValue === "" ? [] : collectionAdj.filter((item) => selectedValues.includes(item.value))}
          menuPlacement={menuPlacement}
        />
      ) : (
        <ReactSelect
          isMulti={isMulti}
          isClearable={false}
          closeMenuOnSelect={closeMenuOnSelect}
          name={name}
          isDisabled={disabled}
          menuPosition={menuPosition}
          // Render the menu into document.body so it isn't clipped by container overflow
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999999 }) }}
          options={collectionAdj}
          onChange={(items) => onChange(items ? (isMulti ? items.map((item) => item.value) : [items.value]) : [])}
          value={collectionAdj.filter((item) => selectedValues.includes(item.value))}
          menuPlacement={menuPlacement}
        />
      )}
      {required && <HiddenInput message={value.length === 0 ? t("pleaseSelectAtLeastOneItem") : null} />}
    </div>
  )
}
